/**
 * Invoice Eligibility Screen
 *
 * Shows per-supplier invoice eligibility results. Columns include
 * Outstanding AR, Max Days Past Due, Max Terms, one column per
 * ineligible document type (amounts excluded), and the final NERB
 * (Net Eligible Receivables Balance).
 *
 * Data is fetched on mount from the Python backend. The hidden
 * "Calculate" button triggers a server-side recalculation.
 */
import { useState, useEffect } from "react";
import { getDocTypesAndCurrencies } from "Redux/invoice/actions";
import { useSelector, useDispatch } from "react-redux";
// ------------------------------------------------------------
import ParameterSummary from "Components/ParameterSummary";
import { getInvoiceData } from "Redux/invoice/actions";
import { calculateAny, getLinearParameters } from "Redux/data/actions";
import DataTable from "Components/DataTable";

// ------------------------------------------------------------
// ------------------------------------------------------------
const Invoice = () => {
  const dispatch = useDispatch();
  const project = useSelector((state) => state.project);
  const { invoice } = useSelector((state) => state.invoice);
  const [, setViewing] = useState(false);
  const [, setCalculating] = useState(false);
  const [showSummary] = useState(false);

  useEffect(() => {
    setViewing(true);
    dispatch(
      getInvoiceData(() => {
        setViewing(false);
      })
    );
  }, [project, dispatch]);

  if (!project) return null;

  const linearParameters = getLinearParameters();
  console.log("Linear Parameters", linearParameters);

  const headers = [
    { text: "Supplier", key: "Supplier", datatype: "string" },
    { text: "Outstanding AR", key: "Outstanding AR", datatype: "float" },
    { text: "Max Days Past Due", key: "Max Days Past Due", datatype: "int"},
    { text: "Max Terms", key: "Max Terms", datatype: "int"}
  ];

  linearParameters["ineligible_document_types"].value.forEach(
    (documentType) => {
      headers.push({
        text: documentType,
        key: documentType,
        datatype: "float",
      });
    }
  );

  headers.push({ text: "NERB", key: "NERB", datatype: "float" });

  return (
    <div>
      <div
        className="button"
        style={{display: "none"}}
        onClick={() => {
          setCalculating(true);
          dispatch(calculateAny("invoice",() => setCalculating(false)));
        }}
      >
        Calculate
      </div>

      {showSummary && (
        <ParameterSummary
          parameterIDs={[
            "max_terms",
            "max_days_past_due",
            "ineligible_currencies",
            "ineligible_document_types",
          ]}
        />
      )}
      {invoice && (
        <DataTable data={invoice.data} headers={headers} reference="Supplier" />
      )}
    </div>
  );
};

export default Invoice;

/** Returns elements in array `a` that are not in array `b`. */
const setDiff = (a, b) => {
  return a.filter((item) => !b.includes(item));
};

// ----------------------------------------------------------------
// Settings modal (currently unused) — Configures invoice eligibility
// parameters: ineligible currencies/document types, due-date vs
// funding-tenor approach, max terms, and max days past due.
// ----------------------------------------------------------------
const Settings = ({ project, onClose }) => { // eslint-disable-line no-unused-vars
  const dispatch = useDispatch();
  const [opacity, setOpacity] = useState(0);
  const [currencies, setCurrencies] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  // retrieve currencies and document types from the frame
  useEffect(() => {
    setOpacity(0.2);
    getDocTypesAndCurrencies((result) => {
      setCurrencies(result.unique_currencies);
      setDocumentTypes(result.unique_document_types);
      setOpacity(1);
    });
  }, []);

  const [hasChanged, setHasChanged] = useState(false);
  const [parameters, setParameters] = useState(null);

  useEffect(() => {
    setParameters(project.invoiceParameters);
  }, [project.invoiceParameters]);

  if (!parameters) return null;

  const remainingCurrencies = setDiff(currencies, parameters.nonCurrencies);
  const remainingDocumentTypes = setDiff(
    documentTypes,
    parameters.nonDocumentTypes
  );

  return (
    <div>
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-content">
          <header className="modal-card-head">
            <p className="modal-card-title">Invoice Eligibility</p>
            <button
              className="delete"
              aria-label="close"
              onClick={onClose}
            ></button>
          </header>

          <section className="modal-card-body">
            <div style={{ opacity: opacity }}>
              <div className="box">
                <table className="table is-fullwidth">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Eligible</th>
                      <th>Ineligible</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th>Currencies</th>
                      <td>
                        <div className="box">
                          <div className="tags">
                            {remainingCurrencies.map((currency) => (
                              <span
                                key={`currency-${currency}`}
                                className="tag is-info is-light is-clickable"
                                onClick={() => {
                                  setParameters({
                                    ...parameters,
                                    nonCurrencies: [
                                      ...parameters.nonCurrencies,
                                      currency,
                                    ],
                                  });
                                  setHasChanged(true);
                                }}
                              >
                                {currency}
                              </span>
                            ))}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="box">
                          <div className="tags">
                            {parameters.nonCurrencies.map((currency) => (
                              <span
                                key={`noncurrency-${currency}`}
                                className="tag is-info is-light is-clickable"
                                onClick={() => {
                                  setParameters({
                                    ...parameters,
                                    nonCurrencies:
                                      parameters.nonCurrencies.filter(
                                        (c) => c !== currency
                                      ),
                                  });
                                  setHasChanged(true);
                                }}
                              >
                                {currency}
                              </span>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>Document Types</th>
                      <td>
                        <div className="box">
                          <div className="tags">
                            {remainingDocumentTypes.map((documentType) => (
                              <span
                                key={`document-${documentType}`}
                                className="tag is-info is-light is-clickable"
                                onClick={() => {
                                  setParameters({
                                    ...parameters,
                                    nonDocumentTypes: [
                                      ...parameters.nonDocumentTypes,
                                      documentType,
                                    ],
                                  });
                                  setHasChanged(true);
                                }}
                              >
                                {documentType}
                              </span>
                            ))}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="box">
                          <div className="tags">
                            {parameters.nonDocumentTypes.map((documentType) => (
                              <span
                                key={`nondocument-${documentType}`}
                                className="tag is-info is-light is-clickable"
                                onClick={() => {
                                  setParameters({
                                    ...parameters,
                                    nonDocumentTypes:
                                      parameters.nonDocumentTypes.filter(
                                        (dt) => dt !== documentType
                                      ),
                                  });
                                  setHasChanged(true);
                                }}
                              >
                                {documentType}
                              </span>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="box">
                <div className="columns">
                  <div className="column">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <input
                        type="radio"
                        name="invoice-type"
                        checked={parameters.approach === "due-date"}
                        onChange={() => {
                          setParameters({
                            ...parameters,
                            approach: "due-date",
                          });
                          setHasChanged(true);
                        }}
                      />
                      &nbsp;
                      <div>Due Date approach</div>
                    </div>
                  </div>
                  <div className="column">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <input
                        type="radio"
                        name="invoice-type"
                        checked={parameters.approach === "funding-tenor"}
                        onChange={() => {
                          setParameters({
                            ...parameters,
                            approach: "funding-tenor",
                          });
                          setHasChanged(true);
                        }}
                      />
                      &nbsp;
                      <div>Funding Tenor approach</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="box">
                <table className="table is-fullwidth">
                  <tbody>
                    <tr>
                      <td>Max Terms [days]</td>
                      <td>
                        <input
                          className="input"
                          type="text"
                          value={parameters.termsA}
                          onChange={(evt) => {
                            const value = evt.target.value.replace(
                              /[^0-9]/g,
                              ""
                            );
                            if (!isNaN(value)) {
                              setParameters({
                                ...parameters,
                                termsA: parseInt(value),
                              });
                              setHasChanged(true);
                            }
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Max Days Past Due</td>
                      <td>
                        <input
                          className="input"
                          type="text"
                          value={parameters.termsB}
                          onChange={(evt) => {
                            const value = evt.target.value.replace(
                              /[^0-9]/g,
                              ""
                            );
                            if (!isNaN(value)) {
                              setParameters({
                                ...parameters,
                                termsB: parseInt(value),
                              });
                              setHasChanged(true);
                            }
                          }}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <footer className="modal-card-foot">
            <div className="buttons">
              <button
                className={`button ${hasChanged ? "is-success" : ""}`}
                disabled={!hasChanged}
                onClick={() => {
                  dispatch({
                    type: "SET_INVOICE_PARAMETERS",
                    invoiceParameters: parameters,
                  });
                  onClose();
                }}
              >
                Save changes
              </button>
              <button className="button" onClick={onClose}>
                Close
              </button>
            </div>
          </footer>
        </div>
      </div>

      <h1>Invoice</h1>
    </div>
  );
};
